import React, { useMemo, useState } from 'react';
import SvgIcon from '@home/components/SvgIcon';
import useLanguage from '@op/hooks/useLanguage';
import CommonCalendar from '@op/components/CommonCalendar';
import { Button, Modal, TextArea } from 'tea-component';
import { useGetCalendarData } from './hooks/useGetCalendarData';
import { CalendarDay } from '@op/components/CommonCalendar/utils';

import './index.scss';
import { CalendarTime, CalendarData } from './type';
import moment from 'moment';

export default function Calendar({ defaultValue }: { defaultValue: string }) {
  const { formatMessage, isEn } = useLanguage();

  // 日历start_time、 end_time
  const [calendarTime, setCalendarTime] = useState<CalendarTime>();
  /**
   * calendarData: 日历全部数据
   * modifyCalendar: 新增、编辑日历时间
   */
  const { calendarData, setCalendarData, modifyCalendar, loading} = useGetCalendarData({
    calendarTime,
  });

  // 弹窗相关
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupData, setPopupData] = useState<CalendarData>({
    date: '',
    content: '',
    isCreate: true,
  });

  // 点击CalendarItem唤起弹窗
  function handlePopup(data: CalendarData) {
    setPopupData(data);
    setPopupVisible(true);
  }

  return (
    <>
      <CommonCalendar
        defaultValue={defaultValue}
        backValue={(value: Array<string>) => {
          setCalendarTime({
            start_time: value[0],
            end_time: value[value.length - 1],
          });
        }}
        title={formatMessage('Publish Calendar')}
        className="dbo-calendar-wrapper"
        loading={loading}
        loadingHeight={760}
      >
        {(value) => (
          <CalendarItem
            {...value}
            data={calendarData}
            handlePopup={handlePopup}
          ></CalendarItem>
        )}
      </CommonCalendar>

      {/*  新增、编辑日历弹窗 */}
      <Modal
        className="dbo-add-publish-modal"
        visible={popupVisible}
        caption={formatMessage(
          popupData.isCreate
            ? 'Add publish schedule for {day}'
            : 'Edit publish schedule for {day}',
          {
            day: isEn
              ? `${moment(popupData?.date).format('MMMM D')}th`
              : `${moment(popupData?.date).format('MM月DD日')}`,
          },
        )}
        onClose={() => setPopupVisible(false)}
      >
        <Modal.Body>
          <TextArea
            size="full"
            value={popupData.content}
            onChange={(value) => {
              setPopupData((p) => ({
                date: p.date,
                content: value,
                isCreate: p.isCreate,
              }));
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            onClick={() => {
              modifyCalendar(popupData);
              setCalendarData((value) => ({
                ...value,
                [popupData.date]: popupData.content,
              }));
              setPopupVisible(false);
            }}
          >
            {formatMessage('Confirm')}
          </Button>
          <Button
            type="weak"
            onClick={() => {
              setPopupVisible(false);
            }}
          >
            {formatMessage('Cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function CalendarItem({
  item,
  hoverShowClassName,
  data,
  handlePopup,
}: {
  item: CalendarDay;
  hoverShowClassName: string;
  data?: { [key: string]: string };
  handlePopup: (data: CalendarData) => void;
}) {
  const { formatMessage } = useLanguage();
  const { desc, isEmpty } = useMemo(() => {
    const { date } = item;
    const value = data?.[date];
    if (value) {
      return {
        desc: value,
        isEmpty: false,
      };
    }

    return { isEmpty: true };
  }, [data]);

  return (
    <div className="dbo-calendar-item-box">
      <div className={hoverShowClassName}>
        {isEmpty ? (
          <Button
            type="link"
            className="dbo-item-add"
            onClick={() => {
              handlePopup({ date: item.date, content: '', isCreate: true });
            }}
          >
            <SvgIcon className="dbo-edit-icon" type="icon_add_game"></SvgIcon>
            {formatMessage('Add Schedule')}
          </Button>
        ) : (
          <Button
            className="dbo-item-update"
            onClick={() => {
              handlePopup({
                date: item.date,
                content: data?.[item.date] ?? '',
                isCreate: false,
              });
            }}
          >
            <SvgIcon className="dbo-edit-icon" type="pencil"></SvgIcon>
          </Button>
        )}
      </div>
      {!isEmpty && <div className="dbo-calendar-show">{desc}</div>}
    </div>
  );
}
